import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Clock, Check, AlertCircle, ArrowRight, ArrowLeft, Star, Gift,
  Eye, Download, Upload, ChevronRight, Zap, BookOpen, Home, Heart, Video
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// BackButton component (inline)
const BackButton = ({ to, label = "Back" }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => to ? navigate(to) : navigate(-1)}
      className="inline-flex items-center gap-2 text-zinc-400 hover:text-yellow-500 transition-colors mb-6 group"
      data-testid="back-button"
    >
      <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
      <span>{label}</span>
    </button>
  );
};

// RushBadge component (inline)
const RushBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full border border-red-500/30">
    <Zap size={12} /> RUSH
  </span>
);

// VideoBookBadge component (inline)
const VideoBookBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full border border-purple-500/30">
    <BookOpen size={12} /> VIDEO BOOK
  </span>
);

// VerticalBadge component (inline)
const VerticalBadge = ({ vertical }) => {
  const isRealEstate = vertical === "real_estate";
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
      isRealEstate 
        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
        : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    }`}>
      {isRealEstate ? <Home size={12} /> : <Heart size={12} />}
      {isRealEstate ? 'Real Estate' : 'Memorial'}
    </span>
  );
};

// StatusBadge component (inline)
const StatusBadge = ({ status }) => {
  const statusMap = {
    pending_payment: { label: "Pending Payment", class: "status-pending" },
    awaiting_upload: { label: "Awaiting Upload", class: "status-awaiting" },
    in_progress: { label: "In Progress", class: "status-progress" },
    review: { label: "Ready for Review", class: "status-review" },
    completed: { label: "Completed", class: "status-completed" },
  };
  const s = statusMap[status] || { label: status, class: "status-pending" };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${s.class}`} data-testid="status-badge">
      {s.label}
    </span>
  );
};

// This component receives useAuth as a prop from App.js
const DashboardPage = ({ useAuth }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [reReferral, setReReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [reCopied, setReCopied] = useState(false);
  const userVertical = user?.vertical || "memorial";
  const isAdmin = user?.role === "admin" || user?.role === "editor";
  // Customers see only their vertical; admins see all
  const [verticalFilter, setVerticalFilter] = useState(isAdmin ? "all" : userVertical);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch orders and referral stats on mount
    (async () => {
      try {
        const [ordersRes, referralRes] = await Promise.all([
          axios.get(`${API}/orders`),
          axios.get(`${API}/referral/stats`).catch(() => ({ data: { has_code: false } }))
        ]);
        setOrders(ordersRes.data);
        setReferralStats(referralRes.data);
        // Fetch RE referral code (additive, won't error if user has no RE orders)
        try {
          const reRes = await axios.get(`${API}/re-referral/my-code`);
          setReReferral(reRes.data);
        } catch { /* ignore — user may not be logged in or doesn't have one yet */ }
      } catch (err) {
        console.error("Failed to fetch data:", err);
      }
      setLoading(false);
    })();
  }, []);

  const copyReferralLink = () => {
    if (referralStats?.code) {
      navigator.clipboard.writeText(`https://still2move.com/checkout?ref=${referralStats.code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const generateReferralCode = async () => {
    try {
      const res = await axios.get(`${API}/referral/my-code`);
      setReferralStats({ has_code: true, ...res.data });
    } catch (err) {
      console.error("Failed to generate referral code:", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto">
        <BackButton to="/" label="Back to Home" />
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold font-serif">
              {user?.role === "editor" ? "Editor Dashboard" : "My Orders"}
            </h1>
            <p className="text-zinc-400 mt-1">Welcome back, {user?.full_name}</p>
          </div>
          {user?.role === "customer" && (
            <button onClick={() => navigate(userVertical === "real_estate" ? "/real-estate" : "/")} className="btn-primary" data-testid="new-order-btn">
              New Order
            </button>
          )}
        </div>

        {/* RE Referral Section — additive, only shows when user has RE referral code */}
        {reReferral && verticalFilter === "real_estate" && (
          <div className="card-dark p-6 mb-8 border border-emerald-500/20 bg-gradient-to-br from-zinc-900 to-zinc-800" data-testid="re-referral-section">
            <div className="flex items-center gap-2 mb-4">
              <Home size={20} className="text-emerald-500" />
              <h3 className="text-lg font-bold">Real Estate Referrals</h3>
            </div>
            
            <div className="text-center mb-6">
              <p className="text-4xl font-bold text-emerald-500">${reReferral.totalSaved || 0}</p>
              <p className="text-zinc-400 text-sm mt-1">Saved Through Referrals</p>
            </div>

            <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-3 mb-4">
              <code className="flex-1 text-emerald-400 font-mono text-lg text-center tracking-wider">{reReferral.code}</code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(reReferral.code);
                  setReCopied(true);
                  setTimeout(() => setReCopied(false), 2000);
                }}
                className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded text-sm font-medium transition"
                data-testid="copy-re-referral-btn"
              >
                {reCopied ? "Copied!" : "Copy"}
              </button>
            </div>

            <div className="text-center text-sm text-zinc-500 mb-4">
              <span className="font-medium text-white">{reReferral.totalReferrals || 0}</span> successful referral{reReferral.totalReferrals !== 1 ? 's' : ''}
            </div>

            <div className="bg-zinc-800/50 rounded-lg p-4 text-xs text-zinc-400 space-y-1.5">
              <p className="flex items-center gap-2"><span className="text-emerald-500 font-bold">1.</span> Share your code with another agent</p>
              <p className="flex items-center gap-2"><span className="text-emerald-500 font-bold">2.</span> They get <span className="text-emerald-400 font-semibold">$25 off</span> their RE video order</p>
              <p className="flex items-center gap-2"><span className="text-emerald-500 font-bold">3.</span> You earn <span className="text-emerald-400 font-semibold">$25 credit</span> after their order is paid</p>
            </div>
          </div>
        )}

        {orders.length === 0 ? (
          <div className="card-dark p-12 text-center">
            <Video className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No orders yet</h2>
            <p className="text-zinc-400 mb-6">Create your first memorial video order.</p>
            <button onClick={() => navigate("/checkout")} className="btn-primary">
              Create Order
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Vertical Filter for Editor/Admin */}
            {(user?.role === "editor" || user?.role === "admin") && (
              <div className="flex items-center gap-3 mb-4">
                <span className="text-sm text-zinc-400">Filter:</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setVerticalFilter("all")}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      verticalFilter === "all" 
                        ? "bg-zinc-700 text-white" 
                        : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                    }`}
                  >
                    All ({orders.length})
                  </button>
                  <button
                    onClick={() => setVerticalFilter("memorial")}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-1.5 ${
                      verticalFilter === "memorial" 
                        ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/50" 
                        : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                    }`}
                  >
                    <Heart size={14} /> Memorial ({orders.filter(o => o.vertical !== "real_estate").length})
                  </button>
                  <button
                    onClick={() => setVerticalFilter("real_estate")}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-1.5 ${
                      verticalFilter === "real_estate" 
                        ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/50" 
                        : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                    }`}
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                      <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                    Real Estate ({orders.filter(o => o.vertical === "real_estate").length})
                  </button>
                </div>
              </div>
            )}
            
            <div className="grid gap-4">
            {orders
              .filter(order => {
                // Customers see all their orders, editors/admins can filter
                if (user?.role === "customer") return true;
                if (verticalFilter === "all") return true;
                if (verticalFilter === "memorial") return order.vertical !== "real_estate";
                return order.vertical === verticalFilter;
              })
              .map((order) => {
              const timeline = order.timeline || {};
              const currentMilestone = timeline.milestones?.find(m => m.is_current);
              const isRush = timeline.is_rush;
              
              return (
                <div
                  key={order.id}
                  className={`card-dark p-6 hover:border-zinc-700 cursor-pointer transition ${isRush ? 'border-red-500/50' : ''}`}
                  onClick={() => navigate(`/orders/${order.id}`)}
                  data-testid={`order-card-${order.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className="font-bold text-lg">{order.order_number}</span>
                        {(user?.role === "editor" || user?.role === "admin") && <VerticalBadge vertical={order.vertical} />}
                        {isRush && <RushBadge />}
                        {order.video_book && <VideoBookBadge />}
                        <StatusBadge status={order.status} />
                      </div>
                      <div className="text-sm text-zinc-400">
                        {order.package_name} • {order.photos?.length || 0}/{order.photo_limit} photos
                        {order.videos?.length > 0 && ` • ${order.videos.length} video(s)`}
                      </div>
                      
                      {/* RUSH Timeline */}
                      {isRush && timeline.start_date && order.status !== "completed" && (
                        <div className="mt-3">
                          <div className="flex items-center gap-2 text-sm">
                            <div className="flex-1 bg-zinc-700 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full transition-all ${timeline.rush_deadline_passed ? 'bg-red-500' : 'bg-orange-500'}`}
                                style={{ width: `${Math.min(100, (timeline.hours_elapsed / 48) * 100)}%` }}
                              ></div>
                            </div>
                            <span className={`text-xs whitespace-nowrap ${timeline.rush_deadline_passed ? 'text-red-400' : 'text-orange-400'}`}>
                              {timeline.rush_hours_remaining > 0 
                                ? `${Math.round(timeline.rush_hours_remaining)}h left`
                                : 'OVERDUE'
                              }
                            </span>
                          </div>
                          <div className="text-xs text-orange-400 mt-1">
                            48hr Deadline: {timeline.rush_deadline_display}
                          </div>
                        </div>
                      )}
                      
                      {/* Standard Timeline Progress */}
                      {!isRush && timeline.milestones && timeline.start_date && order.status !== "completed" && (
                        <div className="mt-3">
                          <div className="flex items-center gap-2 text-sm">
                            <div className="flex-1 bg-zinc-700 rounded-full h-1.5">
                              <div 
                                className="bg-yellow-500 h-1.5 rounded-full transition-all"
                                style={{ width: `${Math.min(100, (timeline.days_elapsed / 5) * 100)}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-zinc-400 whitespace-nowrap">
                              Day {timeline.days_elapsed}/5
                            </span>
                          </div>
                          {currentMilestone && (
                            <div className="text-xs text-yellow-500 mt-1">
                              {currentMilestone.label} • Expected complete: {timeline.milestones[3]?.date}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {order.days_until_deletion !== undefined && order.status === "completed" && !order.video_expired && (
                        <div className={`text-sm mt-2 ${order.days_until_deletion <= 1 ? 'text-red-400' : order.days_until_deletion <= 4 ? 'text-orange-400' : 'text-yellow-400'}`}>
                          <Clock size={14} className="inline mr-1" />
                          {order.days_until_deletion <= 0 
                            ? "⚠️ Expires TODAY!" 
                            : order.days_until_deletion === 1 
                              ? "⚠️ Expires TOMORROW!" 
                              : `${order.days_until_deletion} days until deletion`}
                        </div>
                      )}
                      
                      {order.video_expired && (
                        <div className="text-sm text-zinc-500 mt-2">
                          Video expired
                        </div>
                      )}

                      {order.status === "pending_payment" && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const res = await axios.post(`${API}/checkout/resume/${order.id}`);
                              window.location.href = res.data.checkout_url;
                            } catch (err) {
                              alert(err.response?.data?.detail || "Failed to resume payment");
                            }
                          }}
                          className="mt-3 w-full bg-yellow-500 hover:bg-yellow-400 text-black font-semibold py-2 px-4 rounded-lg text-sm transition"
                          data-testid={`complete-payment-${order.id}`}
                        >
                          Complete Payment
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-lg font-bold text-yellow-500">${order.total_amount?.toLocaleString()}</div>
                        <div className="text-xs text-zinc-500">
                          {new Date(order.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <ChevronRight className="text-zinc-600" />
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default DashboardPage;
